import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Link as RouterLink, useRouteMatch } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import LinearProgress from '@material-ui/core/LinearProgress'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import AddIcon from 'components/material-symbol/icons/Add'
import CampaignOutlinedIcon from 'components/material-symbol/icons/Campaign'
import DeleteOutlinedIcon from 'components/material-symbol/icons/Delete'
import Error from 'components/error'
import { PageHeader } from 'components/page-header'
import { useAuth } from 'lib/auth'
import { useCoachCampaigns } from './api-hooks'
import { StatusBadge } from './badges'
import DeleteDraftDialog from './delete-draft-dialog'
import { STEP_PARAM } from './constants'
import { formatCampaignDate, pluralize } from './format'
import useSettingsStyles from 'pages/pga-coach/settings/settings.styles'
import useStyles from './styles'
import SectionTitle from 'components/section-title'

const campaignPath = (basePath, campaign) =>
  campaign.status === 'DRAFT'
    ? `${basePath}/${campaign.id}/edit`
    : `${basePath}/${campaign.id}`

const DeleteDraftButton = ({ campaign }) => {
  const classes = useStyles()
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  return (
    <>
      <Tooltip title="Delete draft">
        <IconButton
          aria-label="Delete draft"
          data-testid={`campaign-delete-${campaign.id}`}
          className={classes.borderedActionButton}
          onClick={() => setIsDeleteOpen(true)}
        >
          <DeleteOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {isDeleteOpen ? (
        <DeleteDraftDialog
          campaign={campaign}
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
        />
      ) : null}
    </>
  )
}

DeleteDraftButton.propTypes = {
  campaign: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
}

export function CampaignListView({ campaigns, basePath }) {
  const classes = useStyles()
  const settingsClasses = useSettingsStyles()
  const newCampaignButton = (
    <Button
      variant="contained"
      color="primary"
      className={classes.pillButton}
      component={RouterLink}
      to={`${basePath}/new`}
      startIcon={<AddIcon />}
    >
      New Campaign
    </Button>
  )

  return (
    <>
      <PageHeader
        title="Campaigns"
        actions={newCampaignButton}
        fixedMobileActions
        keepActionsInline
      />
      <Box className={classes.pageContent}>
        <Box className={classes.card}>
          <Box className={classes.cardHeader}>
            <SectionTitle>Your campaigns</SectionTitle>
            <Typography className={classes.cardSubtitle}>
              Send one message to a group of students. Each student gets it in
              their chat with you.
            </Typography>
          </Box>
          {campaigns.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography className={classes.emptyTitle}>
                No campaigns yet
              </Typography>
              <Typography className={classes.emptyCopy}>
                Tell students about open lesson times, clinics, or programs, and
                see who books.
              </Typography>
              {newCampaignButton}
            </Box>
          ) : (
            <Box>
              {campaigns.map(campaign => (
                <Box key={campaign.id} className={classes.row}>
                  <Box
                    component={RouterLink}
                    to={campaignPath(basePath, campaign)}
                    className={classes.rowLink}
                    data-testid={`campaign-${campaign.id}`}
                  >
                    <Box className={settingsClasses.rowIcon}>
                      <CampaignOutlinedIcon />
                    </Box>
                    <Box className={classes.rowBody}>
                      <Typography className={classes.rowTitle}>
                        {campaign.name}
                      </Typography>
                      <Typography className={classes.rowDetail}>
                        {formatCampaignDate(campaign)}
                        {campaign.recipientCount > 0
                          ? ` · ${pluralize(
                            campaign.recipientCount,
                            'student',
                          )}`
                          : ''}
                      </Typography>
                    </Box>
                    {campaign.status === 'SENT' ? (
                      <Box className={classes.rowStats}>
                        <Box className={classes.rowStat}>
                          <Typography className={classes.rowStatValue}>
                            {campaign.results.directBookingCount}
                          </Typography>
                          <Typography className={classes.rowStatLabel}>
                            Booked from link
                          </Typography>
                        </Box>
                        <Box className={classes.rowStat}>
                          <Typography className={classes.rowStatValue}>
                            {campaign.results.influencedBookingCount}
                          </Typography>
                          <Typography className={classes.rowStatLabel}>
                            Booked later
                          </Typography>
                        </Box>
                      </Box>
                    ) : null}
                    <StatusBadge status={campaign.status} />
                  </Box>
                  {campaign.status === 'DRAFT' ? (
                    <Box className={classes.rowActions}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disableElevation
                        className={classes.pillButton}
                        component={RouterLink}
                        to={`${basePath}/${campaign.id}/edit?${STEP_PARAM}=review`}
                        data-testid={`campaign-review-${campaign.id}`}
                      >
                        Review / Send
                      </Button>
                      <DeleteDraftButton campaign={campaign} />
                    </Box>
                  ) : null}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </>
  )
}

CampaignListView.propTypes = {
  campaigns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      recipientCount: PropTypes.number.isRequired,
      results: PropTypes.shape({
        directBookingCount: PropTypes.number.isRequired,
        influencedBookingCount: PropTypes.number.isRequired,
      }).isRequired,
    }),
  ).isRequired,
  basePath: PropTypes.string.isRequired,
}

const CampaignList = () => {
  const { url } = useRouteMatch()
  const { user } = useAuth()
  const hasPaidTier = user?.coach?.hasPaidTierSubscription
  const { data, loading, error } = useCoachCampaigns({ skip: !hasPaidTier })

  if (loading) return <LinearProgress color="primary" />
  if (error) return <Error error={error} />

  return (
    <CampaignListView
      campaigns={data?.coachCampaigns || []}
      basePath={url.replace(/\/$/, '')}
    />
  )
}

export default CampaignList
